import { useContext, useEffect, useMemo, useState } from "react";
import { colors } from "../tokens";
import { Props, ThemeContext } from "./Theme.Parts";

export const ThemeProvider = ({ children, mode = "system" }: Props) => {
  const [theme, setTheme] = useState<"dark" | "light">("dark");

  useEffect(() => {
    if (mode === "system") {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      const handleThemeChange = (e: { matches: boolean }) =>
        setTheme(e.matches ? "dark" : "light");

      handleThemeChange(mediaQuery); // Set initial theme
      mediaQuery.addEventListener("change", handleThemeChange);
      return () => mediaQuery.removeEventListener("change", handleThemeChange);
    } else {
      setTheme(mode);
    }
  }, [mode]);

  useEffect(() => {
    document.documentElement.className = theme;
  }, [theme]);

  const cssVariables = useMemo(() => {
    const toCss = (tokens: Record<string, string>) =>
      Object.entries(tokens)
        .map(([key, value]) => `${key}: ${value};`)
        .join("\n");
    const currentTokens = colors[theme];
    return `
      :root { 
        color-scheme: ${theme};
        ${toCss(currentTokens)}
      }`;
  }, [theme]);

  const toggleTheme = () =>
    setTheme((prev) => (prev === "light" ? "dark" : "light"));

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      <style>{cssVariables}</style>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
